/**
 * The Claude (Anthropic) section of the AI board — org-level Claude usage next
 * to the Azure AI estate, from `/claude/overview`.
 *
 * ## The expected state is "not configured", and this section is built for it
 *
 * Norican is on the claude.ai **Team plan**, which has no metrics API at all
 * (Team admins get the in-product dashboard and CSV export only). The backend
 * therefore answers `access_state: "no_credential"` today — with copy naming
 * exactly what unlocks the panel — and the not-configured card below is this
 * section's normal rendering, the same pattern `AiAccessPanel` uses for the
 * Azure states. When an `ANTHROPIC_ADMIN_KEY` (a Claude Console organisation's
 * Admin API key) lands in the backend's environment, the same route starts
 * returning measured numbers and this section lights up with no code change.
 *
 * ## Honesty rules carried over from the rest of the board (§5)
 *
 * - **Unconfigured ≠ empty ≠ zero.** When the state is not `ok`, no tile
 *   renders at all — no strip of zeros, no `$0.00 spent` standing in for "we
 *   could not ask". Every measured figure is nullable and a `null` renders as
 *   an em dash, never as `0`.
 * - **Coverage rides on the payload.** A Console org sees only traffic that
 *   authenticates through it; Team-plan claude.ai usage and Bedrock/Vertex/
 *   Foundry traffic never appear. Those `coverage_notes` are rendered beside
 *   the numbers, because a small figure here is easy to misread as "nobody
 *   uses Claude".
 * - **Privacy is upstream.** The Claude Code figures are org-day totals
 *   aggregated at ingest with a k>=5 cohort floor — no email, name or member
 *   id exists anywhere in the payload for this section to leak.
 */

import { CloudOff, Icon, Info, OctagonAlert } from "@norican/icons";
import {
  Alert,
  Badge,
  Card,
  DescriptionList,
  Freshness,
  Skeleton,
  Statistic,
  Typography,
} from "@norican/react";

import { useClaudeOverview } from "@/lib/api/hooks/claude";
import { formatCurrency } from "@/lib/charts/config";

import { AiErrorsAlert } from "./access-panel";
import { PanelCard, SectionHeading } from "./panel-card";
import { EM_DASH, formatCount, formatCountCompact, formatWindow, staleAfterMs } from "./status";

/** Anchor id, so the section is linkable like the board's other sections. */
export const CLAUDE_ANCHOR = "ai-claude";

/** Copy per non-`ok` access state — the compiler keeps this exhaustive. */
const CLAUDE_STATE_COPY = {
  no_credential: {
    tone: "neutral" as const,
    icon: Info,
    badge: "Not configured",
    title: "Claude metrics are not connected yet",
    remedy: [
      "The claude.ai Team plan has no metrics API — this is a plan limitation, not a fault.",
      "Unlock path chosen: stand up a Claude Console organisation and create an Admin API key (Console → Settings → Admin keys, admin role required).",
      "Deploy the key as ANTHROPIC_ADMIN_KEY in the backend environment — the panel lights up on the next refresh with no code change.",
      "Alternative (not implemented here): a Claude Enterprise upgrade, whose Analytics API uses a different key type.",
    ],
  },
  credential_rejected: {
    tone: "danger" as const,
    icon: OctagonAlert,
    badge: "Credential rejected",
    title: "Anthropic refused the configured key",
    remedy: [
      "Check that ANTHROPIC_ADMIN_KEY holds an Admin API key (sk-ant-admin01-…), not an inference or Enterprise-Analytics key.",
      "If the prefix is right, the key has likely been revoked or belongs to a different organisation — mint a fresh one in Console → Settings → Admin keys.",
    ],
  },
  unavailable: {
    tone: "warning" as const,
    icon: CloudOff,
    badge: "Unavailable",
    title: "The Anthropic Admin API did not answer",
    remedy: ["Not a credential problem — the backend retries on its next refresh."],
  },
} as const;

export function ClaudeSection() {
  const overview = useClaudeOverview();
  const data = overview.data;

  return (
    <section className="flex flex-col gap-4">
      <SectionHeading
        id={CLAUDE_ANCHOR}
        title="Claude (Anthropic)"
        subtitle="Org-level Claude usage from the Anthropic Console Admin API: token volume, USD spend and Claude Code delivery totals. A Console org sees only traffic that authenticates through it — claude.ai Team-plan usage never appears here."
      />

      {overview.isPending ? (
        <Skeleton height="10rem" />
      ) : overview.error != null ? (
        // The backend folds every Anthropic failure into the payload, so a
        // thrown error here means the NOVA API itself did not answer.
        <Alert tone="danger" title="The Claude panel could not be loaded">
          The NOVA API did not answer for `/claude/overview`. This is not an Anthropic state —
          nothing is known about Claude usage right now, which is not the same as zero.
        </Alert>
      ) : data == null ? null : data.access_state !== "ok" ? (
        <ClaudeStateCard
          state={data.access_state}
          statusDetail={data.status_detail}
          configured={data.configured}
          window={data.window}
        />
      ) : (
        <ClaudeFigures data={data} isFetching={overview.isFetching} />
      )}
    </section>
  );
}

interface ClaudeStateCardProps {
  state: keyof typeof CLAUDE_STATE_COPY;
  /** The backend's one-liner, rendered verbatim — it names the exact fix. */
  statusDetail: string;
  configured: boolean;
  window: string;
}

/**
 * The not-configured / refused / unavailable card — the section's normal
 * rendering today. Follows `AiAccessPanel`'s shape: name the state in words,
 * render the backend's own detail verbatim, list the remedy, and say plainly
 * that nothing below it is a zero.
 */
function ClaudeStateCard({ state, statusDetail, configured, window }: ClaudeStateCardProps) {
  const copy = CLAUDE_STATE_COPY[state];
  const facts = [
    { label: "Panel", value: "Claude usage, cost and Claude Code totals" },
    { label: "Window it would cover", value: formatWindow(window) },
    {
      label: "Credential",
      value: configured
        ? "Present — Anthropic was asked and did not accept it"
        : "Absent — nothing was asked of Anthropic",
    },
    {
      label: "What it would unlock",
      value: "Usage & Cost API and Claude Code Analytics API (org totals only)",
    },
  ];

  return (
    <Card variant="outline" accent="left" accentTone={copy.tone}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <Icon icon={copy.icon} size="md" />
            <Typography variant="heading-4" as="h2">
              {copy.title}
            </Typography>
          </div>
          {/* State is never colour-alone (§3.2) — the badge names it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          <Alert tone={copy.tone} title="Anthropic: what happened">
            {statusDetail}
          </Alert>

          <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
            {copy.remedy.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>

          <DescriptionList items={facts} columns={2} />

          <Typography variant="body-sm" tone="muted">
            Nothing was measured, so nothing is shown — not zero tokens, not $0.00 spent, and not an
            empty chart.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}

interface ClaudeFiguresProps {
  data: NonNullable<ReturnType<typeof useClaudeOverview>["data"]>;
  isFetching: boolean;
}

/** The measured state: six org-level figures plus the scope truths beside them. */
function ClaudeFigures({ data, isFetching }: ClaudeFiguresProps) {
  const windowLabel = formatWindow(data.window);
  const code = data.claude_code;
  // A read summary with no member count is a day withheld below the k-floor
  // (fewer than `cohort_min_actors` members) — not an unreadable report.
  const withheld = code != null && code.members_active == null;

  return (
    <PanelCard
      title={
        data.organization_name
          ? `Console organisation: ${data.organization_name}`
          : "Console organisation"
      }
      caption={
        <span className="flex items-center gap-3">
          {data.truncated && <span>every figure is a floor — report paging was capped</span>}
          {data.collected_at && (
            <Freshness
              label="Anthropic read"
              since={data.collected_at}
              staleAfter={staleAfterMs(data.cache_ttl_sec)}
              pulse={isFetching}
            />
          )}
        </span>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Statistic
            label="Input tokens"
            value={formatCountCompact(data.input_tokens_window)}
            size="lg"
            caption={
              data.input_tokens_window == null
                ? "The usage report could not be read — unknown, not zero"
                : `Uncached input tokens, ${windowLabel}`
            }
          />
          <Statistic
            label="Output tokens"
            value={formatCountCompact(data.output_tokens_window)}
            size="lg"
            caption={
              data.output_tokens_window == null
                ? "The usage report could not be read — unknown, not zero"
                : `Generated tokens, ${windowLabel}`
            }
          />
          <Statistic
            label="Spend"
            value={
              data.cost_window_usd == null ? EM_DASH : formatCurrency(data.cost_window_usd, "USD")
            }
            size="lg"
            caption={
              data.cost_window_usd == null
                ? "The cost report could not be read — unknown, not $0.00"
                : `API cost, ${windowLabel} · Priority Tier costs excluded by the API`
            }
          />
          <Statistic
            label="Claude Code members active"
            value={formatCount(code?.members_active)}
            size="lg"
            caption={
              code == null
                ? "The Claude Code report could not be read — unknown, not nobody"
                : withheld
                  ? `Withheld — fewer than ${code.cohort_min_actors} members yesterday, so totals would describe individuals`
                  : "Distinct members yesterday (UTC) — a count only; identities are never collected"
            }
          />
          <Statistic
            label="Claude Code sessions"
            value={formatCount(code?.sessions)}
            size="lg"
            caption={
              withheld
                ? "Withheld below the member floor"
                : code?.sessions == null
                  ? "Not readable from yesterday's rows — unknown, not zero"
                  : `${formatCount(code.commits)} commit(s), ${formatCount(code.pull_requests)} PR(s) via Claude Code yesterday`
            }
          />
          <Statistic
            label="Claude Code est. cost"
            value={
              code?.estimated_cost_usd == null
                ? EM_DASH
                : formatCurrency(code.estimated_cost_usd, "USD")
            }
            size="lg"
            caption={
              withheld
                ? "Withheld below the member floor"
                : code?.estimated_cost_usd == null
                  ? "Not readable from yesterday's rows — unknown, not $0.00"
                  : "Estimated, per Anthropic's model pricing, yesterday (UTC)"
            }
          />
        </div>

        {/* §5: errors surfaced beside the figures they undermine, never swallowed. */}
        <AiErrorsAlert errors={data.errors} />

        {/* The scope truths that stop a small number reading as "nobody uses
            Claude" — they ride on the payload, not on this component's memory. */}
        <ul className="text-fg-muted flex list-disc flex-col gap-1 pl-5 text-sm">
          {data.coverage_notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>
    </PanelCard>
  );
}
